import { useCallback, useEffect, useRef, useState } from "react";
import type { GraphyClient } from "../api/client";
import type { ChangeItem } from "../api/types";

// GY-020 live post-publication updates. Establishes a cursor from the snapshot
// handshake (GY-019), then tails changes. The production transport is SSE; this
// cursor-based polling is the degraded-mode fallback (GY-021) and keeps the
// client model identical. Updates feed the notification center ONLY — they never
// mutate the user's current filters, selection, or open code; counts refresh
// only when the user explicitly asks.

const POLL_MS = 5000;
const FEED_CAP = 50;

export interface LiveUpdates {
  feed: ChangeItem[];
  unread: number;
  markAllRead: () => void;
}

export function useLiveUpdates(client: GraphyClient, projectId: string): LiveUpdates {
  const [feed, setFeed] = useState<ChangeItem[]>([]);
  const [unread, setUnread] = useState(0);
  const cursorRef = useRef<number | null>(null);
  const inFlight = useRef(false);

  const markAllRead = useCallback(() => setUnread(0), []);

  useEffect(() => {
    let live = true;
    cursorRef.current = null;
    setFeed([]);
    setUnread(0);

    const poll = async () => {
      if (inFlight.current || cursorRef.current === null) return;
      inFlight.current = true;
      try {
        const delta = await client.changes({ projectId }, cursorRef.current);
        if (!live) return;
        cursorRef.current = delta.cursor;
        if (delta.changes.length) {
          setFeed((prev) => [...delta.changes.slice().reverse(), ...prev].slice(0, FEED_CAP));
          setUnread((u) => u + delta.changes.length);
        }
      } catch {
        /* transient — keep the last cursor and retry on the next tick */
      } finally {
        inFlight.current = false;
      }
    };

    client
      .changesSnapshot({ projectId })
      .then((snap) => {
        if (live) cursorRef.current = snap.cursor;
      })
      .catch(() => {
        /* no live feed available — stay quiet */
      });

    const id = window.setInterval(poll, POLL_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [client, projectId]);

  return { feed, unread, markAllRead };
}
